import { App } from 'antd';
import { useMsal } from '@azure/msal-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Calendar, CalendarSlot } from '../../../dto/model';
import { scopes } from '../../../authConfig';
import { strings } from '../../../lang';
import { salesAppointmentsService } from '../../../services/sales-appointments-service';
import { GetAccessToken } from '../../../utils/auth-utils';

const UnifiedFlowSalesChannel = 'SalesCall1Lead';

/**
 * Turns whatever the calendar request threw into a message. `salesAppointmentsService.getAvailableSlots`
 * throws an `Error` for transport problems and the raw `{ status, statusText }` object for a non-OK response.
 */
const describeLaneCalendarError = (cause: unknown): string => {
  if (cause instanceof Error && cause.message) {
    return cause.message;
  }

  if (typeof cause === 'object' && cause !== null && 'status' in cause) {
    const { status } = cause as { status?: unknown };
    if (typeof status === 'number') {
      return `${strings.preSalesQuestionnaire.laneCalendarError} (HTTP ${status})`;
    }
  }

  return strings.preSalesQuestionnaire.laneCalendarError;
};

type UsePreSalesBookingLaneCalendarParams = {
  signupId?: string;
  lanes: string[];
};

export type PreSalesBookingLaneCalendar = {
  lane: string;
  slots: CalendarSlot[];
  bookingLinks?: Calendar['bookingLinks'];
  calendarProvider?: Calendar['calendarProvider'];
};

export const usePreSalesBookingLaneCalendar = ({
  signupId,
  lanes,
}: UsePreSalesBookingLaneCalendarParams) => {
  const { notification } = App.useApp();
  const { instance, inProgress } = useMsal();
  const [activeBookingLane, setActiveBookingLane] = useState<string | null>(lanes[0] ?? null);
  const [result, setResult] = useState<PreSalesBookingLaneCalendar | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestSeqRef = useRef(0);
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setActiveBookingLane((current) => (current && lanes.includes(current) ? current : lanes[0] ?? null));
  }, [lanes]);

  const fetchBookingLane = useCallback(async (
    lane: string,
    calendarWeek: string,
    accessToken?: string,
  ): Promise<PreSalesBookingLaneCalendar> => {
    if (!signupId) {
      throw new Error(strings.preSalesQuestionnaire.laneCalendarMissingSignupId);
    }

    const token = accessToken ?? (await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi)).accessToken;
    const calendar = await salesAppointmentsService.getAvailableSlots(
      token,
      calendarWeek,
      '',
      signupId,
      UnifiedFlowSalesChannel,
      undefined,
      undefined,
      lane,
    );

    return {
      lane,
      slots: calendar.slots,
      bookingLinks: calendar.bookingLinks,
      calendarProvider: calendar.calendarProvider,
    };
  }, [inProgress, instance, signupId]);

  const loadSlots = useCallback(async (calendarWeek: string, accessToken: string) => {
    if (!activeBookingLane) {
      return { slots: [] };
    }

    const requestSeq = ++requestSeqRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const laneCalendar = await fetchBookingLane(activeBookingLane, calendarWeek, accessToken);
      if (isMountedRef.current && requestSeq === requestSeqRef.current) {
        setResult(laneCalendar);
      }
      return { slots: laneCalendar.slots };
    } catch (cause: unknown) {
      // The calendar service throws the status object it received rather than an Error, so both shapes are
      // handled here without falling back to `any`.
      const message = describeLaneCalendarError(cause);
      if (isMountedRef.current && requestSeq === requestSeqRef.current) {
        setError(message);
        setResult(null);
      }
      throw cause;
    } finally {
      if (isMountedRef.current && requestSeq === requestSeqRef.current) {
        setIsLoading(false);
      }
    }
  }, [activeBookingLane, fetchBookingLane]);

  const selectBookingLane = useCallback((lane: string) => {
    setActiveBookingLane(lane);
  }, []);

  return {
    lanes,
    activeBookingLane,
    selectBookingLane,
    result,
    slots: result?.slots ?? [],
    isLoading,
    error,
    loadSlots,
    clear: () => { setResult(null); setError(null); },
  };
};
